/**
 * Mejora de imagen de los capitulos.
 *
 * El VPS pasa las paginas por waifu2x y las deja al doble de tamano. Sirve
 * para las fuentes que publican pequeno —desde el capitulo 381 de Black Clover
 * las paginas vienen a 784x1145, antes venian a 1200x1800— y arregla dos cosas
 * de una vez en una pantalla grande: la pagina **llena el alto** (con 784 px
 * de ancho se quedaba en el 82% en un 4K, porque el lector no agranda una
 * imagen mas pequena que el hueco) y **deja de estirarse** (x1,50 antes, x0,92
 * despues). Lo de arriba esta medido con el CSS del lector, no supuesto.
 *
 * Los capitulos los elige el usuario con el boton de la ficha: son catorce
 * minutos de CPU cada uno y no tiene sentido gastarlos en lo que no va a leer.
 *
 * El truco que lo mantiene simple: una pagina mejorada es **otra ruta**. Como
 * todo el lector trabaja con rutas —la cache, el almacen, las descargas—, con
 * cambiar la lista de rutas al abrir el capitulo se mejora todo sin tocar nada
 * mas. Ver `servidor/mejora.py` para la otra mitad.
 */

import { cabecerasAuth, ErrorApi } from './cliente'

export type Estado = 'nada' | 'cola' | 'haciendo' | 'lista' | 'fallo'

/**
 * El interruptor general, por aparato.
 *
 * Viene encendido: si un capitulo esta mejorado es porque lo pediste a mano, y
 * lo raro seria no verlo. Apagarlo sirve para comparar, o para ahorrar datos
 * fuera de casa —una pagina mejorada pesa el doble—.
 */
const CLAVE_USAR = 'yomou.mejora'

export function usarMejora(): boolean {
  return localStorage.getItem(CLAVE_USAR) !== 'no'
}

export function guardarUsarMejora(usar: boolean): void {
  localStorage.setItem(CLAVE_USAR, usar ? 'si' : 'no')
}

export interface EstadoMejora {
  estado: Estado
  hechas?: number
  total?: number
  motivo?: string
}

/** Los dos tamanos que guarda el servidor. Ver `SALIDAS` en `mejora.py`. */
const ANCHOS = [1170, 1568] as const

const RUTA_PAGINA = /^\/?api\/v1\/manga\/(\d+)\/chapter\/(\d+)\/page\/(\d+)/
const RUTA_MEJORADA = /^\/?mejora\/pagina\/(\d+)\/(\d+)\/(\d+)/

export interface ClavePagina {
  manga: number
  capitulo: number
  pagina: number
}

/** Saca (manga, capitulo, pagina) de la ruta de una pagina, sea del tipo que sea. */
export function clavePagina(ruta: string): ClavePagina | null {
  const trozos = RUTA_PAGINA.exec(ruta) ?? RUTA_MEJORADA.exec(ruta)
  if (!trozos) return null
  return { manga: Number(trozos[1]), capitulo: Number(trozos[2]), pagina: Number(trozos[3]) }
}

/**
 * El ancho que de verdad hace falta en esta pantalla.
 *
 * La pagina se encaja en el hueco, asi que manda el lado que primero topa: en
 * el iPhone es el ancho (390 CSS x 3 = 1170 px reales) y en un monitor el
 * alto. Pedir el grande en el movil serian megas para pintar 1170 pixeles.
 */
export function anchoDePantalla(): number {
  const dpr = window.devicePixelRatio || 1
  // 1.46 es la proporcion de una pagina de manga; sirve para pasar el alto
  // disponible a "cuanto ancho me cabria"
  const necesario = Math.min(window.innerWidth * dpr, (window.innerHeight * dpr) / 1.46)
  return necesario > ANCHOS[0] ? ANCHOS[1] : ANCHOS[0]
}

/** La ruta mejorada equivalente, o la misma si no es una pagina de capitulo. */
export function rutaMejorada(ruta: string, ancho = anchoDePantalla()): string {
  const clave = clavePagina(ruta)
  if (!clave) return ruta
  return `/mejora/pagina/${clave.manga}/${clave.capitulo}/${clave.pagina}?ancho=${ancho}`
}

/**
 * La ruta original de Suwayomi.
 *
 * Hace falta para el traductor de bocadillos: el servicio de recap recorta la
 * pagina del original, y no sabe nada de las mejoradas.
 */
export function rutaOriginal(ruta: string): string {
  const trozos = RUTA_MEJORADA.exec(ruta)
  if (!trozos) return ruta
  return `/api/v1/manga/${trozos[1]}/chapter/${trozos[2]}/page/${trozos[3]}`
}

/**
 * Llama al servicio. Cuelga de `/mejora`, que es lo que Caddy protege con la
 * misma autenticacion basica que la app ya manda.
 */
async function pedir<T>(ruta: string, cuerpo?: unknown): Promise<T> {
  let respuesta: Response
  try {
    respuesta = await fetch(ruta, {
      method: cuerpo === undefined ? 'GET' : 'POST',
      headers: { ...cabecerasAuth(), 'Content-Type': 'application/json' },
      body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo),
    })
  } catch {
    throw new ErrorApi('No se pudo conectar con el servidor')
  }
  if (!respuesta.ok) {
    const datos = (await respuesta.json().catch(() => null)) as { error?: string } | null
    throw new ErrorApi(datos?.error ?? `El servidor respondio ${respuesta.status}`, respuesta.status)
  }
  return (await respuesta.json()) as T
}

/** Como estan de mejorados varios capitulos de una serie, en una sola peticion. */
export async function estadosMejora(
  mangaId: number,
  capitulos: number[],
): Promise<Record<number, EstadoMejora>> {
  if (capitulos.length === 0) return {}
  const lista = capitulos.join(',')
  return pedir(`/mejora/estado?manga=${mangaId}&capitulos=${lista}`)
}

/**
 * Los capitulos de una serie que tienen algo de mejora.
 *
 * Para la ficha hace falta esto y no `estadosMejora`: la ficha pinta todos los
 * capitulos de la serie —One Piece tiene mil doscientos— y no se les puede
 * preguntar uno a uno ni meterlos todos en la URL.
 */
export async function listaMejoras(mangaId: number): Promise<Record<number, EstadoMejora>> {
  return pedir(`/mejora/lista?manga=${mangaId}`)
}

/**
 * Pone un capitulo en la cola del servidor.
 *
 * Se manda tambien `capituloId`: con el, el servicio le pide a Suwayomi que
 * resuelva las paginas antes de empezar. Sin eso, un capitulo que no has
 * abierto nunca no tiene paginas resueltas y la primera devuelve 404.
 */
export async function pedirMejora(
  mangaId: number,
  capitulo: number,
  paginas: number,
  capituloId: number,
): Promise<EstadoMejora> {
  return pedir('/mejora/pedir', { manga: mangaId, capitulo, paginas, capituloId })
}

export async function borrarMejora(mangaId: number, capitulo: number): Promise<void> {
  await pedir('/mejora/borrar', { manga: mangaId, capitulo })
}
